import { RequestService } from './../../../core/services/request.service';
import { Component, OnInit, HostListener, ChangeDetectorRef, inject, viewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatTabChangeEvent, MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { environment } from '../../../../environments/environment';
import { UsersClient, CustomerSelectorDto, Auth0User, PermissionDto, AppMetadata, RoleDto, Permission, UserInfoDto, LocationContactDto, LocationsClient } from '../../../core/services/api.service';
import { SnackBarService } from '../../../core/services/snack-bar.service';
import { SpinnerService } from '../../../core/spinner/spinner.svc';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { LocationSelectorDto } from '../../../shared/widgets/location-selector/location-selector.component';
import { Permissions } from '../../../shared/common/permissions';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { forkJoin, lastValueFrom, of } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CustomerSearchComponent } from 'app/shared/widgets/customer-search/customer-search.component';
import { LocationDisplayComponent } from 'app/shared/widgets/location-display/location-display.component';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserCustomersComponent } from './user-customers/user-customers.component';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PermissionCheckerService } from '../../../core/services/permission-checker.service';
import { UserNotificationComponent } from '../../../user-profile/user-notifications/user-notifications.component';
import { BlockUserDialogComponent, BlockUserResult } from '../../../shared/block-user-dialog/block-user-dialog.component';

@Component({
  selector: 'user-modal',
  templateUrl: './user-modal.component.html',
  styleUrls: ['./user-modal.component.scss'],
  imports: [
    MatDialogModule,
    MatTabsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    CustomerSearchComponent,
    LocationDisplayComponent,
    MatSelectModule,
    MatOptionModule,
    MatIconModule,
    MatSlideToggleModule,
    MatTooltipModule,
    UserCustomersComponent,
    MatButtonModule,
    MatProgressSpinnerModule,
    UserNotificationComponent
  ]
})
export class UserModalComponent implements OnInit {
  dialogRef = inject<MatDialogRef<UserModalComponent>>(MatDialogRef);
  private _usersClient = inject(UsersClient);
  private _spinnerService = inject(SpinnerService);
  private _snackBarService = inject(SnackBarService);
  private _locationsClient = inject(LocationsClient);
  private _request = inject(RequestService);
  protected _matDialog = inject(MatDialog);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _permissionCheckerService = inject(PermissionCheckerService);
  data = inject(MAT_DIALOG_DATA);

  readonly tabGroup = viewChild<MatTabGroup>('tabGroup');

  model: Auth0User | undefined;
  errorMessage: string = "";
  isProduction: boolean = false;
  allPermissions: Permission[] = [];
  permissions: Permission[] = [];
  permissionCategories: { category: string, permissions: Permission[] }[] = [];
  userPermissions: PermissionDto[] = [];
  allRoles: RoleDto[] = [];
  roles: RoleDto[] = [];
  selectedRoles: RoleDto[] = [];
  private selectedPermissions: string[] = [];
  isPermissionSaving = false;
  tabGroupIndex = 0;
  rolePermissionsToggle: { roleId: string, isShow: boolean }[] = [];
  isUserCustomerHasChanges = false;
  hasChanges = false;
  defaultOriginLocationContact: LocationContactDto | undefined;
  isDev: boolean = false;
  isManageAll: boolean = false;
  isManageExternal: boolean = false;
  isRolesLoading = false;
  isPermissionsLoading = false;
  
  // Memoization cache for permissions by role.
  private permissionsByRoleCache = new Map<string, { title: string, description: string }[]>();

  constructor() {
    this.isProduction = environment.production;
  }

  @HostListener('window:keyup.esc') onKeyUp() {
    this.dialogRef.close();
  }

  async ngOnInit() {
    if (this.data.userId || this.data.email) {
      await this.loadUser();
    } else {
      this.model = new Auth0User();
      this.model.appMetadata = new AppMetadata();
      this.model.userSettings = new UserInfoDto();

      if (this.data.customerId) {
        this.model.userSettings.customerId = this.data.customerId;
      }

      this.loadRolesAndPermissions();
    }

    this.isDev = await this._permissionCheckerService.hasPermission(Permissions.DEV_ALL);
    this.isManageAll = await this._permissionCheckerService.hasPermission(Permissions.USERS_MANAGEALL);
    this.isManageExternal = await this._permissionCheckerService.hasPermission(Permissions.USERS_MANAGEEXTERNAL);
  }

  private async loadUser() {
    this._spinnerService.show();

    try {
      this.model = await lastValueFrom(this._usersClient.users_GetUser(this.data.userId));
      this.setDefaultOriginLocationContact();
      this.loadRolesAndPermissions();
    } catch (error: any) {
      this._snackBarService.openError('Error loading user: ' + error.message);
    } finally {
      this._spinnerService.hide();
    }
  }

  private loadRolesAndPermissions() {
    this.loadRolesAndUserRoles();
    this.loadPermissionsAndUserPermissions();
  }

  private loadRolesAndUserRoles() {
    this.isRolesLoading = true;
    const rolesRequest = this._usersClient.users_GetAllRoles();
    const userRolesRequest = this.data.userId ? this._usersClient.users_GetUserRoles(this.data.userId) : of<RoleDto[]>([]);
    forkJoin([rolesRequest, userRolesRequest]).subscribe({
      next: ([roles, userRoles]) => {
        this.allRoles = roles;
        this.roles = this.allRoles.filter(role => this.isDev || (!this.isDev && !role.isDevOnly));
        if (this.model?.isASM) {
          this.roles = this.roles.filter(role => !role.isExternalOnly);
        } else {
          this.roles = this.roles.filter(role => {
            const isValidRole = !!role.id && !role.isASMOnly;
            const isUserRole = userRoles.find(x => x.id == role.id);
            return isValidRole || isUserRole;
          });
        }
        this.rolePermissionsToggle = this.roles.map(s => { return { roleId: s.id ?? "", isShow: false } });
        this.selectedRoles = userRoles;
      },
      error: (error) => {
        this._snackBarService.openError('Error loading roles: ' + error.message);
        this.isRolesLoading = false;
      },
      complete: () => {
        this.isRolesLoading = false;
      }
    })
  }

  private loadPermissionsAndUserPermissions() {
    this.isPermissionsLoading = true;
    const permissionsRequest = this._usersClient.users_GetPermissions();
    const userPermissionsRequest = this.data.userId ? this._usersClient.users_GetUserPermissions(this.data.userId) : of<PermissionDto[]>([]);
    forkJoin([permissionsRequest, userPermissionsRequest]).subscribe({
      next: ([permissions, userPermissions]) => {
        this.allPermissions = permissions;
        this.userPermissions = userPermissions;
        this.permissions = this.allPermissions.filter(permission => this.isDev || (!this.isDev && !permission.isDevOnly));
        if (!this.model?.isASM) {
          this.permissions = this.permissions.filter(permission => {
            const isValidPermission = !!permission.key && !permission.isASMOnly;
            const isUserPermission = userPermissions.find(x => x.value == permission.key);
            return isValidPermission || isUserPermission;
          });
        }

        this.selectedPermissions = userPermissions.map(s => s.value ?? "");

        // set category and permission for UI display
        this.permissionCategories = [];
        let category: any = [];
        this.permissions.map((s) => {
          if (category.filter((x: any) => x == s.category).length > 0) {
            return;
          }
          category.push(s.category);
          this.permissionCategories.push({ category: s.category ?? "", permissions: this.permissions.filter(x => x.category == s.category) })
        });

        this.permissionsByRoleCache.clear();
        this._changeDetectorRef.markForCheck();
      },
      error: (error) => {
        this._snackBarService.openError('Error loading permissions: ' + error.message);
        this.isPermissionsLoading = false;
      },
      complete: () => {
        this.isPermissionsLoading = false;
      }
    })
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  isSelectedRole(role: RoleDto) {
    return this.selectedRoles.filter(s => s.id == role.id).length > 0;
  }

  saveUserToRole($event: MatSlideToggleChange, role: RoleDto) {
    if (this.model?.userId) {
      if ($event.checked) {
        this._request.save(this._usersClient.users_AddUserRole(this.model.userId, role.id), () => {
          this.selectedRoles.push(role);
          this.loadPermissionsAndUserPermissions();
        }, "Role")
      } else {
        this._request.save(this._usersClient.users_RemoveUserRole(this.model.userId, role.id), () => {
          this.selectedRoles = this.selectedRoles.filter(s => s.id != role.id);
          this.loadPermissionsAndUserPermissions();
        }, "Role")
      }
    }
  }

  isPermissionRoleBased(permission: Permission) {
    return this.getRolesForPermission(permission.key ?? "").length > 0;
  }

  getRolesForPermission(permissionKey: string): RoleDto[] {
    if (!this.selectedRoles || !this.selectedRoles.length || !this.roles) {
      return [];
    }

    // Get IDs of selected roles
    const selectedRoleIds = this.selectedRoles.map(r => r.id);

    // Find roles from this.roles (which has full permission details) 
    // that are selected and contain the permission
    return this.roles.filter(role => 
      selectedRoleIds.includes(role.id) &&
      (role.permissions ?? []).some(p => p.value === permissionKey)
    );
  }

  getDisabledPermissionTooltip(permission: Permission) {
    const roles = this.getRolesForPermission(permission.key ?? "");
    
    if (!roles || roles.length === 0) {
      return "";
    }

    const roleNames = roles.map(r => r.name).join(', ');
    return `Permission assigned by role${roles.length > 1 ? 's' : ''}: ${roleNames}`;
  }

  getPermissionsByKeys(permissions: PermissionDto[]) {
    if (!permissions || permissions.length <= 0) {
      return [];
    }

    // Create a cache key from the permission values
    const cacheKey = permissions.map(p => p.value).sort().join(',');
    
    // Return cached result if available
    if (this.permissionsByRoleCache.has(cacheKey)) {
      return this.permissionsByRoleCache.get(cacheKey);
    }

    // Build the permission list
    const permissionTitleDescriptions: { title: string, description: string }[] = [];
    permissions.forEach(s => {
      const title = this.getPermissionTitle(s.value ?? "") ?? "";
      const description = this.getPermissionDescription(s.value ?? "") ?? "";
      permissionTitleDescriptions.push({ title, description });
    });

    // Sort and cache the result
    const sorted = permissionTitleDescriptions.sort((a, b) => a.title > b.title ? 1 : -1);
    this.permissionsByRoleCache.set(cacheKey, sorted);
    
    return sorted;
  }

  getPermissionTitle(key: string) {
    return this.allPermissions.find(s => s.key == key)?.title;
  }

  getPermissionDescription(key: string) {
    return this.allPermissions.find(s => s.key == key)?.description;
  }

  toggleExpandStatusRolePermission(roleId: string) {
    var rolePermissionToggle = this.rolePermissionsToggle.find(s => s.roleId == roleId);
    if (rolePermissionToggle) {
      rolePermissionToggle.isShow = !rolePermissionToggle.isShow;
    }
  }

  getExpandStatusRolePermission(roleId: string): boolean {
    return this.rolePermissionsToggle?.find(s => s.roleId == roleId)?.isShow ?? false;
  }

  private nextTab() {
    const tabGroup = this.tabGroup();
    if (tabGroup) {
      tabGroup.selectedIndex = this.tabGroupIndex + 1;
    }
  }

  private setDefaultOriginLocationContact() {
    if (this.model && this.model.userSettings && this.model.userSettings.defaultOriginLocationContactId) {
      this._locationsClient.locations_GetLocationContact(this.model.userSettings.defaultOriginLocationContactId).subscribe((locationContact: LocationContactDto) => {
        this.defaultOriginLocationContact = locationContact;
      })
    }
  }

  saveUser() {
    if (this.model?.userId) {
      this.updateUser();
    } else {
      this.createUser();
    }
  }

  deleteUser() {
    const confirmDialogRef = this._matDialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: { title: "Delete User?", message: `Are you sure you want to delete user <b>${this.model?.email}</b>`, displayMessageAsHTML: true, cancelButtonText: "Cancel", okButtonText: "Yes, delete", okButtonColor: 'warn' },
      disableClose: true
    });

    confirmDialogRef.afterClosed().subscribe(result => {
      if (result && this.model && this.model.userId) {
        this._spinnerService.show();
        this._usersClient.users_DeleteUser(this.model.userId, this.model.email).subscribe(x => {
          this._spinnerService.hide();
          this._snackBarService.open(`User ${this.model?.fullName ?? ""} removed`);
          this.dialogRef.close(true);
        }, err => {
          this._spinnerService.hide();
          this._snackBarService.openError("Error deleting user")
        })
      }
    });
  }

  createUser() {
    if (this.model) {
      this._spinnerService.show();

      if (this.model.userSettings) {
        this.model.userSettings.email = this.model.email ?? "";
      }

      if (this.model.userSettings?.customerId) {
        if (!this.model.appMetadata) {
          this.model.appMetadata = new AppMetadata();
        }
        this.model.appMetadata.customerId = this.model.userSettings.customerId;
        this.model.appMetadata.viewCustomerIds = [this.model.userSettings.customerId];
        this.model.appMetadata.createCustomerIds = [this.model.userSettings.customerId];
        this.model.appMetadata.billCustomerIds = [this.model.userSettings.customerId];
      }

      this._usersClient.users_PostUser(this.model).subscribe((createdUser: Auth0User) => {
        if (this.model) {
          this._snackBarService.open('User ' + createdUser.fullName + ' added successfully');
          this.model.userId = createdUser.userId;
          this.nextTab();
        }
      },
      error => {
        if (error.response.includes("password")) {
          this.errorMessage = "The specified password does not comply with password complexity requirements. Please provide a different password."
        } else {
          this.errorMessage = "An error occurred: " + error.message;
        }
      })
      .add(() => this._spinnerService.hide());
    }
  }

  updateUser() {
    if (this.model) {
      this._spinnerService.show();

      this._usersClient.users_UpdateUser(this.model)
        .subscribe(x => {
          this._snackBarService.open('User ' + this.model?.fullName + ' saved successfully');
          this.dialogRef.close(true);
        },
          error => {
            this._snackBarService.open('Error saving user: ' + error.message);
          })
        .add(() => this._spinnerService.hide());
    }
  }

  customerSelected(customer: CustomerSelectorDto) {
    if (this.model?.userSettings) {
      if (customer) {
        this.model.userSettings.customerId = customer.id;
      }
      else {
        this.model.userSettings.customerId = 0;
      }
    }
    this.hasChanges = true;
  }

  customersSelected(customers: any) {
    if (this.model && this.model.appMetadata) {
      this.model.appMetadata.viewCustomerIds = customers.viewCustomerIds;
      this.model.appMetadata.createCustomerIds = customers.createCustomerIds;
      this.model.appMetadata.billCustomerIds = customers.billCustomerIds;
    }

    if (customers.primaryCustomerId && this.model && this.model.userSettings) {
      // removed default origin location contact if primary customer is changed
      if (customers.primaryCustomerId != this.model.userSettings.customerId && this.model.userSettings.defaultOriginLocationContact) {
        this.model.userSettings.defaultOriginLocationContact = undefined;
      }

      this.model.userSettings.customerId = customers.primaryCustomerId;
    }
    else if (!customers.primaryCustomerId && this.model && this.model.userSettings) {
      this.model.userSettings.customerId = 0;
    }

    this.isUserCustomerHasChanges = customers.hasChanges;
    this.hasChanges = customers.hasChanges;
  }

  defaultOriginSelected(selection: LocationSelectorDto) {
    // Check if previous value is different from current value
    if (this.model?.userSettings?.defaultOriginLocationContactId !== selection?.locationContact?.id) {
      this.hasChanges = true;
    }

    if (this.model?.userSettings) {
      if (selection?.locationContact) {
        this.model.userSettings.defaultOriginLocationContactId = selection.locationContact.id;
      }
      else {
        delete this.model.userSettings.defaultOriginLocationContactId;
        delete this.model.userSettings.defaultOriginLocationContact;
      }
    }
  }

  hasPermission(permissionName: string) {
    return this.selectedPermissions.some(x => x == permissionName)
  }

  addRemovedPermissions(isCheck: boolean, permissionName: string) {
    if (this.model?.userId) {
      this.isPermissionSaving = true;
      if (isCheck) {
        return this._usersClient.users_AddUserPermission(this.model.userId, permissionName).subscribe(result => {
          this.selectedPermissions.push(permissionName);
        }, (error) => {
          this.isPermissionSaving = false;
        }, () => {
          this.isPermissionSaving = false;
        })
      } else {
        return this._usersClient.users_RemoveUserPermission(this.model.userId, permissionName).subscribe(result => {
          this.selectedPermissions = this.selectedPermissions.filter(s => {
            return s != permissionName;
          });
        }, (error) => {
          this.isPermissionSaving = false;
        }, () => {
          this.isPermissionSaving = false;
        })
      }
    }
  }

  tabChanged(tabChangeEvent: MatTabChangeEvent): void {
    this.tabGroupIndex = tabChangeEvent.index;
  }

  blockUser() {
    // show block user dialog
    const blockDialogRef = this._matDialog.open(BlockUserDialogComponent, {
      width: '450px',
      data: { 
        title: "Block User", 
        message: `Please select a reason for blocking user <b>${this.model?.email}</b>`, 
        displayMessageAsHTML: true, 
        cancelButtonText: "Cancel", 
        okButtonText: "Block", 
        okButtonColor: 'warn' 
      },
      disableClose: true
    });

    // if user provides block reason, block user
    blockDialogRef.afterClosed().subscribe((result: BlockUserResult) => {
      if (result && result.blockReason) {
        this._request.save(this._usersClient.users_BlockUser(this.model.userId, result.blockReason), () => {
          this._snackBarService.open(`User ${this.model?.fullName ?? ""} blocked`);
          this.dialogRef.close(true);
        }, "User")
      }
    });
  }

  unblockUser() {
    if (this.model.blocked) {
      // user is blocked, show confirmation dialog to unblock user
      const confirmDialogRef = this._matDialog.open(ConfirmDialogComponent, {
        width: '350px',
        data: { title: "Unblock User?", message: `Are you sure you want to unblock user <b>${this.model?.email}</b>`, displayMessageAsHTML: true, cancelButtonText: "Cancel", okButtonText: "Unblock", okButtonColor: 'warn' },
        disableClose: true
      });

      // if user confirms, unblock user
      confirmDialogRef.afterClosed().subscribe(result => {
        if (result) {
          this._request.save(this._usersClient.users_UnblockUser(this.model.userId), () => {
            this._snackBarService.open(`User ${this.model?.fullName ?? ""} unblocked`);
            this.dialogRef.close(true);
          }, "User")
        }
      });
    }
  }

  isRoleDisabled(role: RoleDto) {
    // Dev permission can edit permissions for all users
    if (this.isDev) {
      return false;
    }
    
    // Manage all permission can edit permissions for all users
    if (this.isManageAll) {
      return false;
    }

    // Manage external permission can edit permissions for non-ASM users
    if (this.isManageExternal && !this.model?.isASM && !role.isASMOnly) {
      return false;
    }

    return true;
  }
  
  isPermissionDisabled(permission: Permission) {
    // If permission is being saved, it should be disabled
    if (this.isPermissionSaving) {
      return true;
    }

    // If permission is assigned by role, it should be disabled
    if (this.isPermissionRoleBased(permission)) {
      return true;
    }

    // Dev permission can edit permissions for all users
    if (this.isDev) {
      return false;
    } else if (permission.isDevOnly) {
      return true;
    }

    // Manage all permission can edit permissions for all users
    if (this.isManageAll) {
      return false;
    }

    // Manage external permission can edit permissions for non-ASM users
    if (this.isManageExternal && !this.model?.isASM) {
      return false;
    }
    
    return true;
  }
}
